import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Alert, Muted, PageHeader, Section } from '@/components/ui'
import {
  createCargo,
  deleteCargo,
  fetchCargo,
  fetchCompetencias,
  fetchDepartamentos,
  updateCargo,
} from './api'
import type { CargoCreatePayload, CargoDetalhe, CargoTagRef } from './types'
import { formatMoney } from './utils'

const NIVEIS = [
  { value: 'operacional', label: 'Operacional' },
  { value: 'administrativo', label: 'Administrativo' },
  { value: 'tecnico', label: 'Técnico' },
  { value: 'supervisao', label: 'Supervisão' },
  { value: 'gerencia', label: 'Gerência' },
  { value: 'direcao', label: 'Direção' },
  { value: 'c_level', label: 'C-Level' },
]

type FormState = {
  nome: string
  area: string
  codigo_cbo: string
  nivel: string
  nivel_grade: string
  salario_min: string
  salario_max: string
  departamento_id: string
  objetivo: string
  responsabilidades: string
  formacao: string
  diferenciais: string
  kpis: string
  ativo: boolean
  tagsTec: CargoTagRef[]
  tagsComp: CargoTagRef[]
}

function emptyForm(): FormState {
  return {
    nome: '',
    area: '',
    codigo_cbo: '',
    nivel: 'administrativo',
    nivel_grade: '',
    salario_min: '',
    salario_max: '',
    departamento_id: '',
    objetivo: '',
    responsabilidades: '',
    formacao: '',
    diferenciais: '',
    kpis: '',
    ativo: true,
    tagsTec: [],
    tagsComp: [],
  }
}

function tagsFromTexto(texto?: string): CargoTagRef[] {
  if (!texto) return []
  return String(texto)
    .split(/[\n•|;]+/)
    .map((s) => s.replace(/^[\-\*\d\.\)\]]+\s*/, '').trim())
    .filter((s) => s.length >= 2)
    .map((nome) => ({ nome }))
}

function formFromCargo(c: CargoDetalhe): FormState {
  const tec = [...(c.tags_tecnicas || [])]
  const comp = [...(c.tags_comportamentais || [])]
  return {
    nome: c.nome || '',
    area: c.area || '',
    codigo_cbo: c.codigo_cbo || '',
    nivel: c.nivel || 'administrativo',
    nivel_grade: c.nivel_grade != null ? String(c.nivel_grade) : '',
    salario_min: c.salario_min != null && c.salario_min !== '' ? String(c.salario_min) : '',
    salario_max: c.salario_max != null && c.salario_max !== '' ? String(c.salario_max) : '',
    departamento_id: c.departamento_id || '',
    objetivo: c.objetivo || c.descricao || '',
    responsabilidades: c.responsabilidades || '',
    formacao: c.formacao || '',
    diferenciais: c.diferenciais || '',
    kpis: c.kpis || '',
    ativo: c.ativo !== false,
    tagsTec: tec.length ? tec : tagsFromTexto(c.competencias_tecnicas),
    tagsComp: comp.length ? comp : tagsFromTexto(c.competencias_comportamentais),
  }
}

function numOrNull(v: string): number | null {
  const t = v.trim()
  if (!t) return null
  const n = Number(t.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

function payloadFromForm(f: FormState): CargoCreatePayload {
  return {
    nome: f.nome.trim(),
    area: f.area.trim(),
    codigo_cbo: f.codigo_cbo.trim(),
    nivel: f.nivel || 'administrativo',
    nivel_grade: f.nivel_grade.trim() ? Number(f.nivel_grade) : null,
    salario_min: numOrNull(f.salario_min),
    salario_max: numOrNull(f.salario_max),
    departamento_id: f.departamento_id || null,
    objetivo: f.objetivo.trim(),
    responsabilidades: f.responsabilidades.trim(),
    formacao: f.formacao.trim(),
    diferenciais: f.diferenciais.trim(),
    kpis: f.kpis.trim(),
    ativo: f.ativo,
    tags_tecnicas_input: f.tagsTec.map((t) => (t.id ? { id: t.id, nome: t.nome } : t.nome)),
    tags_comportamentais_input: f.tagsComp.map((t) =>
      t.id ? { id: t.id, nome: t.nome } : t.nome,
    ),
  }
}

function TagEditor({
  label,
  tags,
  sugestoes,
  disabled,
  onChange,
}: {
  label: string
  tags: CargoTagRef[]
  sugestoes: Array<{ id: string; nome: string }>
  disabled?: boolean
  onChange: (next: CargoTagRef[]) => void
}) {
  const [draft, setDraft] = useState('')
  const selecionadas = useMemo(
    () => new Set(tags.map((t) => t.nome.trim().toLowerCase())),
    [tags],
  )

  function add(nome: string, id?: string) {
    const n = nome.trim()
    if (!n) return
    if (selecionadas.has(n.toLowerCase())) return
    onChange([...tags, id ? { id, nome: n } : { nome: n }])
    setDraft('')
  }

  const disponiveis = sugestoes
    .filter((s) => !selecionadas.has(s.nome.trim().toLowerCase()))
    .slice(0, 12)

  return (
    <label className="hi-cargo-tags-field">
      {label}
      <div className={`hi-cargo-tags${disabled ? ' is-ro' : ''}`}>
        <div className="hi-cargo-chips">
          {tags.length === 0 ? <Muted>Nenhuma tag</Muted> : null}
          {tags.map((t, i) => (
            <span key={`${t.id || t.nome}-${i}`} className="hi-cargo-chip">
              {t.nome}
              {!disabled ? (
                <button
                  type="button"
                  className="hi-cargo-chip-x"
                  aria-label={`Remover ${t.nome}`}
                  onClick={() => onChange(tags.filter((_, idx) => idx !== i))}
                >
                  ×
                </button>
              ) : null}
            </span>
          ))}
        </div>
        {!disabled ? (
          <>
            <div className="hi-cargo-tag-add">
              <input
                type="text"
                value={draft}
                list={`hi-cargo-dl-${label}`}
                placeholder="Digite e Enter para adicionar…"
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    const hit = sugestoes.find(
                      (s) => s.nome.trim().toLowerCase() === draft.trim().toLowerCase(),
                    )
                    add(hit?.nome || draft, hit?.id)
                  }
                }}
              />
              <datalist id={`hi-cargo-dl-${label}`}>
                {sugestoes.map((s) => (
                  <option key={s.id} value={s.nome} />
                ))}
              </datalist>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  const hit = sugestoes.find(
                    (s) => s.nome.trim().toLowerCase() === draft.trim().toLowerCase(),
                  )
                  add(hit?.nome || draft, hit?.id)
                }}
              >
                +
              </button>
            </div>
            {disponiveis.length ? (
              <div className="hi-cargo-sugs">
                {disponiveis.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="hi-cargo-sug"
                    onClick={() => add(s.nome, s.id)}
                  >
                    {s.nome}
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </label>
  )
}

export function CargoFormPage() {
  const { id } = useParams<{ id: string }>()
  const isNovo = !id || id === 'novo'
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { canEdit } = useAuth()
  const editable = canEdit('cargos')
  const [form, setForm] = useState<FormState>(emptyForm())
  const [msg, setMsg] = useState('')
  const [erro, setErro] = useState('')

  const cargoQ = useQuery({
    queryKey: ['rh', 'cargo', id],
    queryFn: () => fetchCargo(id!),
    enabled: !isNovo,
  })

  const depsQ = useQuery({
    queryKey: ['rh', 'departamentos', 'opts'],
    queryFn: fetchDepartamentos,
  })

  const tecQ = useQuery({
    queryKey: ['rh', 'competencias', 'tecnica', 'banco'],
    queryFn: () => fetchCompetencias({ tipo: 'tecnica', ativos: true, por_pagina: 500 }),
    enabled: editable,
  })

  const compQ = useQuery({
    queryKey: ['rh', 'competencias', 'comportamental', 'banco'],
    queryFn: () => fetchCompetencias({ tipo: 'comportamental', ativos: true, por_pagina: 500 }),
    enabled: editable,
  })

  useEffect(() => {
    if (cargoQ.data) setForm(formFromCargo(cargoQ.data))
  }, [cargoQ.data])

  const salvar = useMutation({
    mutationFn: async () => {
      const payload = payloadFromForm(form)
      if (!payload.nome) throw new Error('Informe o nome do cargo.')
      if (isNovo) return createCargo(payload)
      return updateCargo(id!, payload)
    },
    onSuccess: async (dados) => {
      setMsg(isNovo ? 'Cargo cadastrado.' : 'Cargo atualizado.')
      setErro('')
      await qc.invalidateQueries({ queryKey: ['rh', 'cargos'] })
      if (isNovo && dados?.id) {
        navigate(`/app/cargos/${dados.id}`, { replace: true })
      } else if (dados) {
        setForm(formFromCargo(dados))
        await qc.invalidateQueries({ queryKey: ['rh', 'cargo', id] })
      }
    },
    onError: (err) => {
      setErro(err instanceof ApiError || err instanceof Error ? err.message : 'Falha ao salvar.')
    },
  })

  const remover = useMutation({
    mutationFn: () => deleteCargo(id!),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['rh', 'cargos'] })
      navigate('/app/cargos')
    },
    onError: (err) => {
      setErro(err instanceof ApiError ? err.message : 'Falha ao remover.')
    },
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!editable) return
    salvar.mutate()
  }

  const titulo = isNovo ? 'Novo cargo' : form.nome || 'Cargo'
  const ro = !editable

  return (
    <section className="hi-page hi-cargos-page hi-cargo-form-page">
      <PageHeader
        title={titulo}
        subtitle="Descritivo completo: objetivo, responsabilidades, formação, competências e KPIs."
        actions={
          <>
            <Link className="btn btn-secondary" to="/app/cargos">
              Voltar
            </Link>
            {!isNovo && editable ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={remover.isPending}
                onClick={() => {
                  if (!window.confirm('Remover este cargo do catálogo?')) return
                  remover.mutate()
                }}
              >
                Remover
              </button>
            ) : null}
          </>
        }
      />

      {msg ? <Alert tone="info">{msg}</Alert> : null}
      {erro ? <Alert tone="danger">{erro}</Alert> : null}
      {cargoQ.isLoading ? <Muted>Carregando…</Muted> : null}
      {cargoQ.isError ? (
        <Alert tone="danger">
          {cargoQ.error instanceof ApiError ? cargoQ.error.message : 'Cargo não encontrado.'}
        </Alert>
      ) : null}

      {(isNovo || cargoQ.data) && !cargoQ.isLoading ? (
        <form className="card card-body hi-stack" onSubmit={onSubmit}>
          <Section title="Identificação">
            <div className="hi-form-grid">
              <label>
                Nome do cargo *
                <input
                  required
                  disabled={ro}
                  value={form.nome}
                  onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                />
              </label>
              <label>
                Área
                <input
                  disabled={ro}
                  value={form.area}
                  onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))}
                />
              </label>
              <label>
                CBO
                <input
                  disabled={ro}
                  value={form.codigo_cbo}
                  onChange={(e) => setForm((f) => ({ ...f, codigo_cbo: e.target.value }))}
                />
              </label>
              <label>
                Nível
                <select
                  disabled={ro}
                  value={form.nivel}
                  onChange={(e) => setForm((f) => ({ ...f, nivel: e.target.value }))}
                >
                  {NIVEIS.map((n) => (
                    <option key={n.value} value={n.value}>
                      {n.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Grau (1–5)
                <input
                  type="number"
                  min={1}
                  max={5}
                  disabled={ro}
                  value={form.nivel_grade}
                  onChange={(e) => setForm((f) => ({ ...f, nivel_grade: e.target.value }))}
                />
              </label>
              <label>
                Departamento
                <select
                  disabled={ro}
                  value={form.departamento_id}
                  onChange={(e) => setForm((f) => ({ ...f, departamento_id: e.target.value }))}
                >
                  <option value="">—</option>
                  {(depsQ.data || []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.nome}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Salário mín.
                <input
                  type="number"
                  step="0.01"
                  disabled={ro}
                  value={form.salario_min}
                  onChange={(e) => setForm((f) => ({ ...f, salario_min: e.target.value }))}
                />
              </label>
              <label>
                Salário máx.
                <input
                  type="number"
                  step="0.01"
                  disabled={ro}
                  value={form.salario_max}
                  onChange={(e) => setForm((f) => ({ ...f, salario_max: e.target.value }))}
                />
              </label>
              <label className="hi-cargo-check">
                <input
                  type="checkbox"
                  disabled={ro}
                  checked={form.ativo}
                  onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))}
                />
                Ativo
              </label>
            </div>
            {!isNovo &&
            cargoQ.data &&
            (cargoQ.data.salario_hc_min != null || cargoQ.data.salario_hc_max != null) &&
            !form.salario_min &&
            !form.salario_max ? (
              <Muted>
                Faixa observada nos colaboradores anexados:{' '}
                {formatMoney(cargoQ.data.salario_hc_min)} –{' '}
                {formatMoney(cargoQ.data.salario_hc_max)}
              </Muted>
            ) : null}
          </Section>

          <Section title="Descritivo">
            <div className="hi-form-grid">
              <label className="hi-span-2">
                Objetivo do cargo
                <textarea
                  rows={3}
                  disabled={ro}
                  value={form.objetivo}
                  onChange={(e) => setForm((f) => ({ ...f, objetivo: e.target.value }))}
                />
              </label>
              <label className="hi-span-2">
                Principais responsabilidades
                <textarea
                  rows={5}
                  disabled={ro}
                  value={form.responsabilidades}
                  onChange={(e) => setForm((f) => ({ ...f, responsabilidades: e.target.value }))}
                />
              </label>
              <label className="hi-span-2">
                Formação
                <textarea
                  rows={3}
                  disabled={ro}
                  value={form.formacao}
                  onChange={(e) => setForm((f) => ({ ...f, formacao: e.target.value }))}
                />
              </label>
            </div>
          </Section>

          <Section title="Competências">
            <div className="hi-form-grid">
              <TagEditor
                label="Competências técnicas"
                tags={form.tagsTec}
                sugestoes={tecQ.data?.dados || []}
                disabled={ro}
                onChange={(tagsTec) => setForm((f) => ({ ...f, tagsTec }))}
              />
              <TagEditor
                label="Competências comportamentais"
                tags={form.tagsComp}
                sugestoes={compQ.data?.dados || []}
                disabled={ro}
                onChange={(tagsComp) => setForm((f) => ({ ...f, tagsComp }))}
              />
            </div>
          </Section>

          <Section title="Diferenciais e KPIs">
            <div className="hi-form-grid">
              <label className="hi-span-2">
                Diferenciais
                <textarea
                  rows={3}
                  disabled={ro}
                  value={form.diferenciais}
                  onChange={(e) => setForm((f) => ({ ...f, diferenciais: e.target.value }))}
                />
              </label>
              <label className="hi-span-2">
                KPIs
                <textarea
                  rows={3}
                  disabled={ro}
                  value={form.kpis}
                  onChange={(e) => setForm((f) => ({ ...f, kpis: e.target.value }))}
                />
              </label>
            </div>
          </Section>

          {!isNovo && cargoQ.data?.headcount != null ? (
            <Muted>Colaboradores ativos neste cargo: {cargoQ.data.headcount}</Muted>
          ) : null}

          {editable ? (
            <div className="hi-form-actions">
              <button type="submit" className="btn btn-primary" disabled={salvar.isPending}>
                {salvar.isPending ? 'Salvando…' : isNovo ? 'Cadastrar' : 'Salvar descritivo'}
              </button>
            </div>
          ) : (
            <Alert tone="info">Sem permissão de edição neste módulo.</Alert>
          )}
        </form>
      ) : null}
    </section>
  )
}

export default CargoFormPage
